import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaCodeBranch,
  FaTableList,
  FaClipboardList,
  FaArrowsRotate,
  FaChalkboardUser,
  FaArrowRight,
  FaBullseye,
} from "react-icons/fa6";
import PageHero from "../components/PageHero";

const SERVICES = [
  { icon: <FaBullseye />, t: "Learning outcomes development", d: "Define program-level outcomes that are measurable, meaningful, and aligned with the standards your program is assessed against." },
  { icon: <FaCodeBranch />, t: "Curriculum alignment", d: "Map courses, units, and assessments to outcomes so the curriculum tells one coherent story." },
  { icon: <FaTableList />, t: "Assessment planning", d: "Design direct and indirect assessments that capture what students actually learn — without burying faculty in busywork." },
  { icon: <FaClipboardList />, t: "Program review support", d: "Prepare and run program reviews that lead to specific, useful change rather than shelved reports." },
  { icon: <FaArrowsRotate />, t: "Continuous improvement strategies", d: "Build the small, sustainable cycles that turn assessment data into program-level improvement." },
  { icon: <FaChalkboardUser />, t: "Outcome-based education support", d: "Help faculty and leadership operationalize outcome-based education in everyday teaching and assessment." },
];

export default function Programs() {
  useEffect(() => {
    document.title = 'Programs — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Program Development"
        title={
          <>
            Designed, aligned, assessed —{" "}
            <span className="italic text-brand-moss">programs that deliver outcomes.</span>
          </>
        }
        lead="We partner with faculty, departments, and education leaders to design new programs and strengthen existing ones — with a clear thread from learning outcomes to curriculum to assessment."
      />

      {/* Services */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 rounded-3xl overflow-hidden border border-ink/10">
            {SERVICES.map((s) => (
              <div key={s.t} className="bg-bone p-7 hover:bg-white transition-colors">
                <div className="flex items-center justify-end">
                  <span className="text-brand-aubergine text-xl">{s.icon}</span>
                </div>
                <h3 className="mt-5 font-display text-xl text-ink">{s.t}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-bar">How we work</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                Embedded with your team — not delivered from the outside in.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-slate2 leading-relaxed text-lg">
                Program development engagements are scoped collaboratively. We meet
                you at your stage — whether that is a brand-new program, a mid-cycle
                refresh, or preparation for a major review.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: "Discover", d: "Understand your program, learners, faculty, and the standards you operate under." },
              { t: "Design", d: "Co-create outcomes, alignment, and assessment that fit your real context." },
              { t: "Embed", d: "Support the rollout, assessment cycle, and continuous improvement work going forward." },
            ].map((p) => (
              <div
                key={p.t}
                className="bg-white rounded-2xl border border-ink/10 p-7"
              >
                <div className="h-px w-10 bg-brand-aubergine mb-4" />
                <h3 className="font-display text-2xl text-ink">{p.t}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bone">
        <div className="container-page pb-20 pt-16">
          <div className="rounded-3xl bg-ink text-bone p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Partner on program work
              </div>
              <h3 className="mt-3 font-display text-2xl lg:text-3xl leading-tight">
                Strengthen a program with us.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
            >
              Start a conversation <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
